import React, { useMemo, useState, createElement } from 'react';
import { useAppStore } from '../../../../hooks/useAppStore';
import { PageLayout } from '../shared/PageLayout';
import { ConnectionBadge } from '../shared/ConnectionBadge';
import { StatCard } from '../shared/StatCard';
import { Avatar } from '../shared/Avatar';
import { SUBJECT_LABELS, formatDate, timeAgo } from '../shared/utils';
import { ActivityIcon, FilterIcon, SearchIcon, ChevronDownIcon, ClockIcon, BookOpenIcon, TrendingUpIcon, DownloadIcon } from 'lucide-react';
type LogType = 'session' | 'all';
export function LogsPage() {
  const {
    accounts,
    getLearnerData
  } = useAppStore();
  // accounts is AccountsState (object) — extract flat array
  const allAccounts = useMemo(() => accounts?.accountsById ? Object.values(accounts.accountsById) : [], [accounts]);
  // Generate real sessions from learner data
  const sessions = useMemo(() => {
    const allSessions: any[] = [];
    allAccounts.filter(a => a.role === 'learner').forEach(learner => {
      const data = getLearnerData(learner.id);
      if (!data?.familyMastery) return;
      // Group attempts by family/subject to simulate sessions
      Object.values(data.familyMastery).forEach(mastery => {
        if (mastery.attempts > 0) {
          const subjectId = mastery.familyId.split('-')[0];
          // Create a mock session based on mastery data
          allSessions.push({
            id: `sess-${learner.id}-${mastery.familyId}`,
            accountId: learner.id,
            subjectId: subjectId || 'UNK',
            startTime: mastery.lastSeen || Date.now() - Math.random() * 86400000 * 7,
            answers: Array.from({
              length: Math.min(10, mastery.attempts)
            }).map((_, i) => ({
              isCorrect: i < mastery.correct / mastery.attempts * Math.min(10, mastery.attempts)
            }))
          });
        }
      });
    });
    return allSessions.sort((a, b) => b.startTime - a.startTime);
  }, [allAccounts, getLearnerData]);
  const [search, setSearch] = useState('');
  const [subjectFilter, setSubjectFilter] = useState('');
  const [dateFilter, setDateFilter] = useState<'all' | '1d' | '7d' | '30d'>('7d');
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 50;
  const dateThreshold = useMemo(() => {
    const now = Date.now();
    if (dateFilter === '1d') return now - 86400000;
    if (dateFilter === '7d') return now - 7 * 86400000;
    if (dateFilter === '30d') return now - 30 * 86400000;
    return 0;
  }, [dateFilter]);
  const allSubjects = useMemo(() => {
    return Array.from(new Set(sessions.map(s => s.subjectId).filter(Boolean))).sort();
  }, [sessions]);
  const filteredSessions = useMemo(() => {
    return sessions.filter(s => {
      const matchDate = s.startTime >= dateThreshold;
      const matchSubject = !subjectFilter || s.subjectId === subjectFilter;
      const account = allAccounts.find(a => a.id === s.accountId);
      const matchSearch = !search || (account?.displayName ?? '').toLowerCase().includes(search.toLowerCase()) || s.subjectId?.toLowerCase().includes(search.toLowerCase()) || s.id?.toLowerCase().includes(search.toLowerCase());
      return matchDate && matchSubject && matchSearch;
    }).sort((a, b) => b.startTime - a.startTime);
  }, [sessions, allAccounts, dateThreshold, subjectFilter, search]);
  const paginatedSessions = useMemo(() => {
    return filteredSessions.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  }, [filteredSessions, page]);
  const totalPages = Math.ceil(filteredSessions.length / PAGE_SIZE);
  const stats = useMemo(() => {
    const now = Date.now();
    const today = sessions.filter(s => s.startTime >= now - 86400000);
    const week = sessions.filter(s => s.startTime >= now - 7 * 86400000);
    const avgScore = filteredSessions.length > 0 ? Math.round(filteredSessions.reduce((sum, s) => {
      const correct = s.answers?.filter((a: any) => a.isCorrect).length ?? 0;
      const total = s.answers?.length ?? 0;
      return sum + (total > 0 ? correct / total * 100 : 0);
    }, 0) / filteredSessions.length) : 0;
    return {
      today: today.length,
      week: week.length,
      total: sessions.length,
      avgScore
    };
  }, [sessions, filteredSessions]);
  const exportCsv = () => {
    const rows = [['ID', 'Leerling', 'Vak', 'Score %', 'Vragen', 'Datum'], ...filteredSessions.map(s => {
      const account = allAccounts.find(a => a.id === s.accountId);
      const correct = s.answers?.filter((a: any) => a.isCorrect).length ?? 0;
      const total = s.answers?.length ?? 0;
      const pct = total > 0 ? Math.round(correct / total * 100) : 0;
      return [s.id, account?.displayName ?? '?', s.subjectId, pct, total, formatDate(s.startTime)];
    })];
    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], {
      type: 'text/csv'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sessies-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };
  return <PageLayout title="Activiteitenlog" description="Overzicht van alle oefensessies" actions={<div className="flex items-center gap-2" data-id="element-747">
          <ConnectionBadge status="live" data-id="element-748" />
          <button onClick={exportCsv} className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-749">
            <DownloadIcon className="w-4 h-4" data-id="element-750" />
            Export CSV
          </button>
        </div>} data-id="element-746">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-id="element-751">
        <StatCard label="Vandaag" value={stats.today} icon={<ClockIcon className="w-5 h-5" data-id="element-753" />} color="blue" data-id="element-752" />
        <StatCard label="Deze week" value={stats.week} icon={<ActivityIcon className="w-5 h-5" data-id="element-755" />} color="emerald" data-id="element-754" />
        <StatCard label="Totaal sessies" value={stats.total} icon={<BookOpenIcon className="w-5 h-5" data-id="element-757" />} color="purple" data-id="element-756" />
        <StatCard label="Gem. score" value={`${stats.avgScore}%`} icon={<TrendingUpIcon className="w-5 h-5" data-id="element-759" />} color="amber" data-id="element-758" />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-4" data-id="element-760">
        <div className="relative flex-1 min-w-[200px]" data-id="element-761">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-762" />
          <input type="text" placeholder="Zoek op leerling of vak..." value={search} onChange={e => {
          setSearch(e.target.value);
          setPage(0);
        }} className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-763" />
        </div>

        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1" data-id="element-764">
          {(['all', '1d', '7d', '30d'] as const).map(d => <button key={d} onClick={() => {
          setDateFilter(d);
          setPage(0);
        }} className={`px-3 py-1 text-xs rounded-md transition-colors ${dateFilter === d ? 'bg-white text-gray-900 font-medium shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-765">
              {d === 'all' ? 'Alles' : d === '1d' ? 'Vandaag' : d === '7d' ? '7 dagen' : '30 dagen'}
            </button>)}
        </div>

        <div className="relative" data-id="element-766">
          <FilterIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-767" />
          <select value={subjectFilter} onChange={e => {
          setSubjectFilter(e.target.value);
          setPage(0);
        }} className="pl-9 pr-8 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none bg-white" data-id="element-768">
            <option value="" data-id="element-769">Alle vakken</option>
            {allSubjects.map(s => <option key={s} value={s} data-id="element-770">
                {SUBJECT_LABELS[s] ?? s}
              </option>)}
          </select>
          <ChevronDownIcon className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" data-id="element-771" />
        </div>
      </div>

      {/* Results count */}
      <div className="text-xs text-gray-500 mb-3" data-id="element-772">
        {filteredSessions.length.toLocaleString('nl')} sessies gevonden
        {filteredSessions.length !== sessions.length && ` (van ${sessions.length.toLocaleString('nl')} totaal)`}
      </div>

      {/* Log table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-773">
        <div className="overflow-x-auto" data-id="element-774">
          <table className="w-full text-sm" data-id="element-775">
            <thead data-id="element-776">
              <tr className="text-xs text-gray-500 border-b border-gray-100 bg-gray-50" data-id="element-777">
                <th className="text-left px-4 py-3 font-medium" data-id="element-778">Leerling</th>
                <th className="text-left px-4 py-3 font-medium" data-id="element-779">Vak</th>
                <th className="text-right px-4 py-3 font-medium" data-id="element-780">Vragen</th>
                <th className="text-right px-4 py-3 font-medium" data-id="element-781">Score</th>
                <th className="text-right px-4 py-3 font-medium" data-id="element-782">Datum</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50" data-id="element-783">
              {paginatedSessions.length === 0 ? <tr data-id="element-784">
                  <td colSpan={5} className="px-4 py-12 text-center text-sm text-gray-400" data-id="element-785">
                    Geen sessies gevonden
                  </td>
                </tr> : paginatedSessions.map(session => {
              const account = allAccounts.find(a => a.id === session.accountId);
              const correct = session.answers?.filter((a: any) => a.isCorrect).length ?? 0;
              const total = session.answers?.length ?? 0;
              const pct = total > 0 ? Math.round(correct / total * 100) : 0;
              return <tr key={session.id} className="hover:bg-gray-50 transition-colors" data-id="element-786">
                      <td className="px-4 py-3" data-id="element-787">
                        <div className="flex items-center gap-2" data-id="element-788">
                          <Avatar name={account?.displayName ?? '?'} data-id="element-789" />
                          <span className="text-gray-800" data-id="element-790">
                            {account?.displayName ?? <span className="text-gray-400 italic" data-id="element-791">
                                Onbekend
                              </span>}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3" data-id="element-792">
                        <span className="text-gray-600" data-id="element-793">
                          {SUBJECT_LABELS[session.subjectId] ?? session.subjectId}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right text-gray-600 tabular-nums" data-id="element-794">
                        {total}
                      </td>
                      <td className="px-4 py-3 text-right" data-id="element-795">
                        <div className="flex items-center justify-end gap-2" data-id="element-796">
                          <div className="w-12 h-1.5 bg-gray-100 rounded-full overflow-hidden" data-id="element-797">
                            <div className={`h-1.5 rounded-full ${pct >= 80 ? 'bg-emerald-400' : pct >= 60 ? 'bg-amber-400' : 'bg-red-400'}`} style={{
                        width: `${pct}%`
                      }} data-id="element-798" />
                          </div>
                          <span className={`text-xs font-medium tabular-nums ${pct >= 80 ? 'text-emerald-600' : pct >= 60 ? 'text-amber-600' : 'text-red-600'}`} data-id="element-799">
                            {pct}%
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right text-xs text-gray-400 tabular-nums" data-id="element-800">
                        {timeAgo(session.startTime)}
                      </td>
                    </tr>;
            })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between" data-id="element-801">
            <span className="text-xs text-gray-500" data-id="element-802">
              Pagina {page + 1} van {totalPages}
            </span>
            <div className="flex items-center gap-2" data-id="element-803">
              <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} className="px-3 py-1 text-xs border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50 transition-colors" data-id="element-804">
                Vorige
              </button>
              <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} className="px-3 py-1 text-xs border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50 transition-colors" data-id="element-805">
                Volgende
              </button>
            </div>
          </div>}
      </div>
    </PageLayout>;
}